import { useState } from 'react';
import { useCreateDmPractice } from '../../../../../hooks/usePracticeDm';
import { useIdempotencyKey } from '../../../../../hooks/useIdempotencyKey';
import type { DmPracticeOption } from '../../../../../services/practiceDm';
import { buildDmPracticeDraft, validateDmPracticeForm, type DmPracticeForm } from './dmForms.ts';
import { dmErrorMessage } from './dmErrorMessage';

// Lives in the modal, not the form pane, so resubmitting the same form reuses its idempotency key.
export function useDmPracticeCreate(
    practiceId: string,
    initialForm: DmPracticeForm,
    onCreated: (practice: DmPracticeOption) => void
) {
    const [isOpen, setIsOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [showErrors, setShowErrors] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const create = useCreateDmPractice(practiceId);
    const keyFor = useIdempotencyKey();

    const submit = async () => {
        if (Object.keys(validateDmPracticeForm(form)).length > 0) {
            setShowErrors(true);
            return;
        }
        setSubmitError(null);
        const draft = buildDmPracticeDraft(form);
        try {
            const practice = await create.mutateAsync({
                ...draft,
                idempotencyKey: keyFor(JSON.stringify(draft))
            });
            onCreated(practice);
            setIsOpen(false);
        } catch (error) {
            setSubmitError(dmErrorMessage(error, 'Could not create the DM practice.'));
        }
    };

    return {
        isOpen,
        open: () => setIsOpen(true),
        close: () => {
            setIsOpen(false);
            setSubmitError(null);
        },
        form,
        setForm,
        errors: showErrors ? validateDmPracticeForm(form) : {},
        submitError,
        submit: () => void submit(),
        isCreating: create.isPending
    };
}

export type DmPracticeCreate = ReturnType<typeof useDmPracticeCreate>;
